import React, { useState, useEffect, useMemo } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    Chip,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    IconButton,
    Tooltip,
    CircularProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    LinearProgress
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
    Download as DownloadIcon,
    PictureAsPdf as PdfIcon,
    TableChart as ExcelIcon,
    Visibility as ViewIcon,
    Close as CloseIcon,
    Assessment as AssessmentIcon,
    CheckCircle as CheckCircleIcon,
    Pending as PendingIcon,
    PlayArrow as InProgressIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import AssignmentService from '../../../Services/assignment.service';
import UserService from '../../../Services/user.service';
import Constants from '../../../assests/Constants/constants';
import UserAvatar from '../../Widgets/UserAvatar/UserAvatar.js';
import AutocompleteSelect from '../../Widgets/AutocompleteSelect/AutocompleteSelect';

// Styled Components
const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
    maxHeight: 300,
    overflowX: 'auto',
    flex: 1,
    '& .MuiTable-root': {
        borderCollapse: 'separate',
        borderSpacing: 0
    },
    '& *': {
        boxSizing: 'border-box'
    }
}));

const StyledTable = styled(Table)(({ theme }) => ({
    minWidth: 600,
    '& *': {
        boxSizing: 'border-box'
    }
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    textAlign: 'center',
    padding: '4px 2px',
    fontSize: '0.75rem',
    '& *': {
        boxSizing: 'border-box'
    }
}));

const StyledTableHeadCell = styled(TableCell)(({ theme }) => ({
    textAlign: 'center',
    fontWeight: 'bold',
    backgroundColor: '#f5f5f5',
    fontSize: '0.75rem',
    padding: '6px 2px',
    '& *': {
        boxSizing: 'border-box'
    }
}));

const StyledTableRow = styled(TableRow)(({ theme, isEven }) => ({
    backgroundColor: isEven ? '#fafafa' : '#ffffff',
    '&:hover': {
        backgroundColor: '#f0f8ff'
    },
    '& *': {
        boxSizing: 'border-box'
    }
}));

const StyledCard = styled(Card)(({ theme }) => ({
    height: '100%',
    minHeight: 400,
    maxHeight: 500,
    '& *': {
        boxSizing: 'border-box'
    }
}));

// Styled Cards for Summary Statistics
const TotalAssignmentsCard = styled(Card)(({ theme }) => ({
    backgroundColor: '#e3f2fd',
    minHeight: 30,
    elevation: 2,
    '& *': {
        boxSizing: 'border-box !important'
    }
}));

const TodoAssignmentsCard = styled(Card)(({ theme }) => ({
    backgroundColor: '#fce4ec',
    minHeight: 80,
    elevation: 2,
    '& *': {
        boxSizing: 'border-box !important'
    }
}));

const InProgressAssignmentsCard = styled(Card)(({ theme }) => ({
    backgroundColor: '#fff3e0',
    minHeight: 80,
    elevation: 2,
    '& *': {
        boxSizing: 'border-box !important'
    }
}));

const DoneAssignmentsCard = styled(Card)(({ theme }) => ({
    backgroundColor: '#e8f5e8',
    minHeight: 80,
    elevation: 2,
    '& *': {
        boxSizing: 'border-box !important'
    }   
}));

const SummaryCardContent = styled(CardContent)(({ theme }) => ({
    textAlign: 'center',
    padding: '8px 8px',
    '& *': {
        boxSizing: 'border-box !important'
    }
}));

// Styled Grid Components
const SummaryGridContainer = styled(Grid)(({ theme }) => ({
    '& *': {
        boxSizing: 'border-box !important'
    }
}));

const SummaryGridItem = styled(Grid)(({ theme }) => ({
    '& *': {
        boxSizing: 'border-box !important'
    }
}));

const SummaryCardsContainer = styled(Grid)(({ theme }) => ({
    '& *': {
        boxSizing: 'border-box !important'
    }
}));

// Styled Dialog Components
const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
    backgroundColor: '#FF9800',
    color: 'white',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    textAlign: "left",
    position: "relative",
    fontWeight: "bold",
    '& *': {
        boxSizing: 'border-box !important'
    }
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
    padding: theme.spacing(3),
    marginTop: "20px",
    '& *': {
        boxSizing: 'border-box !important'
    }
}));

const StyledDialogActions = styled(DialogActions)(({ theme }) => ({
    justifyContent: "center",
    gap: "20px",
    padding: "16px",
    '& *': {
        boxSizing: 'border-box !important'
    }
}));

// Styled Icon Button
const StyledCloseIconButton = styled(IconButton)(({ theme }) => ({
    color: 'white',
    '& *': {
        boxSizing: 'border-box !important'
    }
}));

// Styled Typography Components
const StyledSectionTitle = styled(Typography)(({ theme }) => ({
    textAlign: "left",
    margin: "0 0 2px 0",
    fontWeight: "bold",
    '& *': {
        boxSizing: 'border-box !important'
    }
}));

const StyledSummaryNumber = styled(Typography)(({ theme }) => ({
    marginBottom: theme.spacing(0.5),
    '& *': {
        boxSizing: 'border-box !important'
    }
}));

const StyledSummaryLabel = styled(Typography)(({ theme }) => ({
    fontSize: '0.7rem',
    '& *': {
        boxSizing: 'border-box !important'
    }
}));

const StyledFilterLabel = styled(Typography)(({ theme }) => ({
    display: "flex",
    justifyContent: "flex-end",
    gap: 1,
    flexDirection: "column",
    '& *': {
        boxSizing: 'border-box !important'
    }
}));

const StyledFilterContainer = styled(Box)(({ theme }) => ({
    direction: "ltr",
    '& *': {
        boxSizing: 'border-box !important'
    }
}));

const StyledFiltersRow = styled(Box)(({ theme }) => ({
    display: "flex",
    gap: 2,
    '& *': {
        boxSizing: 'border-box !important'
    }
}));

const StyledFilterBox = styled(Box)(({ theme }) => ({
    flex: 1,
    '& *': {
        boxSizing: 'border-box !important'
    }
}));

const StyledFiltersContainer = styled(Box)(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    gap: 2,
    '& *': {
        boxSizing: 'border-box !important'
    }
}));

// Styled Button Components
const StyledCancelButton = styled(Button)(({ theme }) => ({
    fontWeight: "bold",
    padding: "10px 20px",
    fontSize: "14px",
    border: "2px solid #757575",
    color: "#424242",
    "&:hover": {
        backgroundColor: "#f5f5f5",
    },
    '& *': {
        boxSizing: 'border-box !important'
    }
}));

const StyledPdfButton = styled(Button)(({ theme }) => ({
    fontWeight: "bold",
    padding: "10px 24px",
    fontSize: "14px",
    background: "linear-gradient(45deg, #f44336 30%, #d32f2f 90%)",
    color: "#fff",
    boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
    "&:hover": {
        background: "linear-gradient(45deg, #d32f2f 30%, #b71c1c 90%)",
    },
    '& *': {
        boxSizing: 'border-box !important'
    }
}));

const StyledExcelButton = styled(Button)(({ theme }) => ({
    fontWeight: "bold",
    padding: "10px 24px",
    fontSize: "14px",
    background: "linear-gradient(45deg, #4caf50 30%, #388e3c 90%)",
    color: "#fff",
    boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
    "&:hover": {
        background: "linear-gradient(45deg, #388e3c 30%, #2e7d32 90%)",
    },
    '& *': {
        boxSizing: 'border-box !important'
    }
}));

// Styled Card Components for Sections
const StyledSectionCard = styled(Card)(({ theme }) => ({
    elevation: 2,
    '& *': {
        boxSizing: 'border-box !important'
    }
}));

const StyledSectionCardContent = styled(CardContent)(({ theme }) => ({
    '& *': {
        boxSizing: 'border-box !important'
    }
}));

// Styled Box Components
const StyledHeaderBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 2,
    '& *': {
        boxSizing: 'border-box !important'
    }
}));

const StyledCardContent = styled(CardContent)(({ theme }) => ({
    padding: theme.spacing(1.5),
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    '& *': {
        boxSizing: 'border-box'
    }
}));

const StyledDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialog-paper': {
        borderRadius: 12,
        minHeight: '85vh',
        maxHeight: '95vh',
        width: '95vw',
        maxWidth: '1400px',
        direction: 'inherit',
        '& *': {
            boxSizing: 'border-box'
        }
    }
}));

const StyledRootContainer = styled(Box)(({ theme }) => ({
    '& *': {
        boxSizing: 'border-box'
    }
}));

const AssignmentReportManager = ({ open, onClose, assignments, subAccountants, caseIdOptions, categoryOptions, startDate, endDate }) => {
    const { t, i18n } = useTranslation();
    const direction = i18n.dir();
    const [loading, setLoading] = useState(false);
    const [reportFilters, setReportFilters] = useState({
        caseId: null,
        assigneeId: null,
        status: null
    });
    const [generatedReport, setGeneratedReport] = useState(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const [user, setUser] = useState(null);
    const [token, setToken] = useState('');

    useEffect(() => {
        const fetchUserData = async () => {
            const currentUser = await UserService.getCurrentUser();
            const currentToken = await UserService.getCurrentToken();
            setUser(currentUser);
            setToken(currentToken);
        };
        fetchUserData();
    }, []);

    const filteredAssignments = useMemo(() => {
        let filtered = assignments;

        if (reportFilters.caseId) {
            filtered = filtered.filter(a => a.caseId === reportFilters.caseId);
        }

        if (reportFilters.assigneeId) {
            filtered = filtered.filter(a => a.assigneeId === reportFilters.assigneeId);
        }

        if (reportFilters.status !== null) {
            filtered = filtered.filter(a => a.status === reportFilters.status);
        }

        return filtered;
    }, [assignments, reportFilters]);

    const todoAssignments = useMemo(() => 
        filteredAssignments.filter(a => a.status === Constants.StatusEnum.ToDo), 
        [filteredAssignments]
    );

    const inProgressAssignments = useMemo(() => 
        filteredAssignments.filter(a => a.status === Constants.StatusEnum.InProgress), 
        [filteredAssignments]
    );

    const doneAssignments = useMemo(() => 
        filteredAssignments.filter(a => a.status === Constants.StatusEnum.Done), 
        [filteredAssignments]
    );

    const handleDownloadPdf = async () => {
        if (!user?.accountantId) {
            toast.error(t('UserNotAuthenticated'));
            return;
        }

        setLoading(true);
        try {
            const request = {
                accountantId: user.accountantId,
                startDate: startDate || dayjs().startOf('month').toDate(),
                endDate: endDate || dayjs().endOf('month').toDate(),
                caseId: reportFilters.caseId || null,
                assigneeId: reportFilters.assigneeId || null,
                status: reportFilters.status
            };

            await AssignmentService.GenerateAssignmentReportPdf(request, token);
            toast.success(t('PdfDownloadedSuccessfully'));
        } catch (error) {
            console.error('Error downloading PDF:', error);
            toast.error(t('ErrorDownloadingPdf'));
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadExcel = async () => {
        if (!user?.accountantId) {
            toast.error(t('UserNotAuthenticated'));
            return;
        }

        setLoading(true);
        try {
            const request = {
                accountantId: user.accountantId,
                startDate: startDate || dayjs().startOf('month').toDate(),
                endDate: endDate || dayjs().endOf('month').toDate(),
                caseId: reportFilters.caseId || null,
                assigneeId: reportFilters.assigneeId || null,
                status: reportFilters.status
            };

            await AssignmentService.GenerateAssignmentReportExcel(request, token);
            toast.success(t('ExcelDownloadedSuccessfully'));
        } catch (error) {
            console.error('Error downloading Excel:', error);
            toast.error(t('ErrorDownloadingExcel'));
        } finally {
            setLoading(false);
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case Constants.StatusEnum.Done:
                return <CheckCircleIcon color="success" />;
            case Constants.StatusEnum.InProgress:
                return <InProgressIcon color="primary" />;
            case Constants.StatusEnum.ToDo:
                return <PendingIcon color="warning" />;
            default:
                return <PendingIcon color="disabled" />;
        }
    };

    const getPriorityColor = (priority) => {
        switch (priority) {
            case Constants.PriorityEnum.Critical:
                return 'error';
            case Constants.PriorityEnum.High:
                return 'warning';
            case Constants.PriorityEnum.Medium:
                return 'info';
            case Constants.PriorityEnum.Low:
                return 'success';
            default:
                return 'default';
        }
    };

    const getPriorityLabel = (priority) => {
        switch (priority) {
            case Constants.PriorityEnum.Low:
                return 'נמוכה';
            case Constants.PriorityEnum.Medium:
                return 'בינונית';
            case Constants.PriorityEnum.High:
                return 'גבוהה';
            case Constants.PriorityEnum.Critical:
                return 'דחופה';
            default:
                return 'נמוכה';
        }
    };

    const getStatusLabel = (status) => {
        switch (status) {
            case Constants.StatusEnum.ToDo:
                return 'לביצוע';
            case Constants.StatusEnum.InProgress:
                return 'בתהליך';
            case Constants.StatusEnum.Done:
                return 'הושלם';
            default:
                return 'לביצוע';
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case Constants.StatusEnum.ToDo:
                return 'error';
            case Constants.StatusEnum.InProgress:
                return 'warning';
            case Constants.StatusEnum.Done:
                return 'success';
            default:
                return 'default';
        }
    };

    const getAssigneeName = (assigneeId) => {
        const assignee = subAccountants.find(sa => sa.subAccountantId === assigneeId);
        return assignee ? assignee.name : "ללא אחראי";
    };

    const getClientName = (caseId) => {
        const client = caseIdOptions.find(c => c.value === caseId);
        return client ? client.label : "משרד";
    };

    const renderAssignmentTable = (assignments, title, color) => (
        <Grid item xs={12} md={4}>
            <StyledCard elevation={2}>
                <StyledCardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="h6" color={color} sx={{ fontWeight: 'bold' }}>
                            {title} ({assignments.length})
                        </Typography>
                    </Box>
                    
                    <StyledTableContainer component={Paper} elevation={1}>
                        <StyledTable>
                            <TableHead>
                                <TableRow>
                                    <StyledTableHeadCell sx={{ minWidth: 100 }}>{t('Summary')}</StyledTableHeadCell>
                                    <StyledTableHeadCell sx={{ minWidth: 80 }}>{t('Assignee')}</StyledTableHeadCell>
                                    <StyledTableHeadCell sx={{ minWidth: 80 }}>{t('Client')}</StyledTableHeadCell>
                                    <StyledTableHeadCell sx={{ minWidth: 70 }}>{t('Priority')}</StyledTableHeadCell>
                                    <StyledTableHeadCell sx={{ minWidth: 70 }}>{t('Status')}</StyledTableHeadCell>
                                    <StyledTableHeadCell sx={{ minWidth: 80 }}>{t('DueDate')}</StyledTableHeadCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {assignments.slice(0, 5).map((assignment, index) => (
                                    <StyledTableRow 
                                        key={assignment.id} 
                                        hover
                                        isEven={index % 2 === 0}
                                    >
                                        <StyledTableCell>
                                            <Typography variant="body2" sx={{ 
                                                maxWidth: 90, 
                                                overflow: 'hidden', 
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                fontSize: '0.7rem'
                                            }}>
                                                {assignment.summary}
                                            </Typography>
                                        </StyledTableCell>
                                        <StyledTableCell>
                                            <UserAvatar name={getAssigneeName(assignment.assigneeId)}  />
                                        </StyledTableCell>
                                        <StyledTableCell>
                                            <UserAvatar name={getClientName(assignment.caseId)}  />
                                        </StyledTableCell>
                                        <StyledTableCell>
                                            <Chip 
                                                label={getPriorityLabel(assignment.priority)}
                                                size="small"
                                                color={getPriorityColor(assignment.priority)}
                                                sx={{ fontWeight: 'bold', fontSize: '0.6rem', minWidth: 50, height: 20 }}
                                            />
                                        </StyledTableCell>
                                        <StyledTableCell>
                                            <Chip 
                                                label={getStatusLabel(assignment.status)}
                                                size="small"
                                                color={getStatusColor(assignment.status)}
                                                sx={{ fontWeight: 'bold', fontSize: '0.6rem', minWidth: 50, height: 20 }}
                                            />
                                        </StyledTableCell>
                                        <StyledTableCell>
                                            <Typography variant="body2" sx={{ fontSize: '0.7rem' }}>
                                                {assignment.dueDate ? dayjs(assignment.dueDate).format('MMM DD') : '-'}
                                            </Typography>
                                        </StyledTableCell>
                                    </StyledTableRow>
                                ))}
                                {assignments.length > 5 && (
                                    <TableRow>
                                        <StyledTableCell colSpan={6} align="center">
                                            <Typography variant="body2" color="textSecondary">
                                                +{assignments.length - 5} {t('More')}
                                            </Typography>
                                        </StyledTableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </StyledTable>
                    </StyledTableContainer>
                </StyledCardContent>
            </StyledCard>
        </Grid>
    );

    return (
        <StyledDialog 
            open={open} 
            onClose={onClose}
            maxWidth="xl"
            fullWidth
            disableScrollLock 
            hideBackdrop 
            disableAutoFocus 
            disableEnforceFocus 
            disableRestoreFocus
        >
            <StyledDialogTitle>
                <StyledCloseIconButton onClick={onClose}>
                    <CloseIcon />
                </StyledCloseIconButton>
                <StyledHeaderBox>
                    <AssessmentIcon />
                    <Typography variant="h6">{t('AssignmentReportManager')}</Typography>
                </StyledHeaderBox>
            </StyledDialogTitle>

            <StyledDialogContent dir={direction}>
                <StyledRootContainer display="flex" flexDirection="column" gap={2} sx={{ textAlign: "left", marginTop: "10px" }}>
                    {/* Summary Statistics - Individual Cards */}
                    <SummaryGridContainer item xs={12}>
                        <SummaryCardsContainer container spacing={1}>
                            <SummaryGridItem item xs={3} md={3}>
                                <TotalAssignmentsCard>
                                    <SummaryCardContent>
                                        <StyledSummaryNumber variant="h5" color="primary">
                                            {filteredAssignments.length}
                                        </StyledSummaryNumber>
                                        <StyledSummaryLabel variant="caption" color="textSecondary">
                                            {t('TotalAssignments')}
                                        </StyledSummaryLabel>
                                    </SummaryCardContent>
                                </TotalAssignmentsCard>
                            </SummaryGridItem>
                            <SummaryGridItem item xs={6} md={3}>
                                <TodoAssignmentsCard>
                                    <SummaryCardContent>
                                        <StyledSummaryNumber variant="h5" color="error.main">
                                            {todoAssignments.length}
                                        </StyledSummaryNumber>
                                        <StyledSummaryLabel variant="caption" color="textSecondary">
                                            {t('ToDo')}
                                        </StyledSummaryLabel>
                                    </SummaryCardContent>
                                </TodoAssignmentsCard>
                            </SummaryGridItem>
                            <SummaryGridItem item xs={6} md={3}>
                                <InProgressAssignmentsCard>
                                    <SummaryCardContent>
                                        <StyledSummaryNumber variant="h5" color="warning.main">
                                            {inProgressAssignments.length}
                                        </StyledSummaryNumber>
                                        <StyledSummaryLabel variant="caption" color="textSecondary">
                                            {t('InProgress')}
                                        </StyledSummaryLabel>
                                    </SummaryCardContent>
                                </InProgressAssignmentsCard>
                            </SummaryGridItem>
                            <SummaryGridItem item xs={6} md={3}>
                                <DoneAssignmentsCard>
                                    <SummaryCardContent>
                                        <StyledSummaryNumber variant="h5" color="success.main">
                                            {doneAssignments.length}
                                        </StyledSummaryNumber>
                                        <StyledSummaryLabel variant="caption" color="textSecondary">
                                            {t('Done')}
                                        </StyledSummaryLabel>
                                    </SummaryCardContent>
                                </DoneAssignmentsCard>
                            </SummaryGridItem>
                        </SummaryCardsContainer>
                    </SummaryGridContainer>

                    {/* Report Configuration */}
                    <StyledSectionCard>
                        <StyledSectionCardContent>
                            <StyledSectionTitle variant="h6" gutterBottom color="primary">
                                {t('ReportConfiguration')}
                            </StyledSectionTitle>
                            
                            <StyledFiltersContainer>
                                {/* Filters */}
                                <StyledFiltersRow>
                                    <StyledFilterBox>
                                        <StyledFilterLabel>
                                            <Box component="strong">    {t('Client')}:</Box>
                                            <StyledFilterContainer>
                                                <AutocompleteSelect
                                                    options={[
                                                        { value: null, label: t('AllClients') },
                                                        ...caseIdOptions.map(option => ({
                                                            value: option.value,
                                                            label: option.label
                                                        }))
                                                    ]}
                                                    selectedValues={
                                                        reportFilters.caseId
                                                            ? (() => {
                                                                const foundClient = caseIdOptions.find(c => c.value === reportFilters.caseId);
                                                                return foundClient 
                                                                    ? { value: foundClient.value, label: foundClient.label }
                                                                    : { value: null, label: t('AllClients') };
                                                              })()
                                                            : { value: null, label: t('AllClients') }
                                                    }
                                                    onChange={(event, values) => {
                                                        setReportFilters(prev => ({
                                                            ...prev,
                                                            caseId: values?.value || null
                                                        }));
                                                    }}
                                                    placeholder={t('SelectClient')}
                                                    isMultiple={false}
                                                    width="300px"
                                                    isDisabled={false}
                                                />
                                            </StyledFilterContainer>
                                        </StyledFilterLabel>
                                    </StyledFilterBox>

                                    <StyledFilterBox>
                                        <StyledFilterLabel>
                                            <Box component="strong">{t('Assignee')}:</Box>
                                            <StyledFilterContainer>
                                                <AutocompleteSelect
                                                    options={[
                                                        { value: null, label: t('AllAssignees') },
                                                        ...subAccountants.map(assignee => ({
                                                            value: assignee.subAccountantId,
                                                            label: assignee.name
                                                        }))
                                                    ]}
                                                    selectedValues={
                                                        reportFilters.assigneeId
                                                            ? (() => {
                                                                const foundAssignee = subAccountants.find(sa => sa.subAccountantId === reportFilters.assigneeId);
                                                                return foundAssignee 
                                                                    ? { value: foundAssignee.subAccountantId, label: foundAssignee.name }
                                                                    : { value: null, label: t('AllAssignees') };
                                                              })()
                                                            : { value: null, label: t('AllAssignees') }
                                                    }
                                                    onChange={(event, values) => {
                                                        setReportFilters(prev => ({
                                                            ...prev,
                                                            assigneeId: values?.value || null
                                                        }));
                                                    }}
                                                    placeholder={t('SelectAssignee')}
                                                    isMultiple={false}
                                                    width="300px"
                                                    isDisabled={false}
                                                />
                                            </StyledFilterContainer>
                                        </StyledFilterLabel>
                                    </StyledFilterBox>

                                    <StyledFilterBox>
                                        <StyledFilterLabel>
                                            <Box component="strong">{t('Status')}:</Box>
                                            <StyledFilterContainer>
                                                <AutocompleteSelect
                                                    options={[
                                                        { value: null, label: t('AllStatuses') },
                                                        { value: Constants.StatusEnum.ToDo, label: t('ToDo') },
                                                        { value: Constants.StatusEnum.InProgress, label: t('InProgress') },
                                                        { value: Constants.StatusEnum.Done, label: t('Done') }
                                                    ]}
                                                    selectedValues={
                                                        reportFilters.status !== null
                                                            ? { value: reportFilters.status, label: t(reportFilters.status === Constants.StatusEnum.ToDo ? 'ToDo' : reportFilters.status === Constants.StatusEnum.InProgress ? 'InProgress' : 'Done') }
                                                            : { value: null, label: t('AllStatuses') }
                                                    }
                                                    onChange={(event, values) => {
                                                        setReportFilters(prev => ({
                                                            ...prev,
                                                            status: values?.value
                                                        }));
                                                    }}
                                                    placeholder={t('SelectStatus')}
                                                    isMultiple={false}
                                                    width="300px"
                                                    isDisabled={false}
                                                />
                                            </StyledFilterContainer>
                                        </StyledFilterLabel>
                                    </StyledFilterBox>
                                </StyledFiltersRow>
                            </StyledFiltersContainer>
                        </StyledSectionCardContent>
                    </StyledSectionCard>

                    {/* Assignment Sections */}
                    <StyledSectionCard>
                        <StyledSectionCardContent>
                            <StyledSectionTitle variant="h6" gutterBottom color="primary">
                                {t('Assignments')}
                            </StyledSectionTitle>
                            <Grid container spacing={2}>
                                {renderAssignmentTable(todoAssignments, t('ToDo'), 'warning.main')}
                                {renderAssignmentTable(inProgressAssignments, t('InProgress'), 'primary.main')}
                                {renderAssignmentTable(doneAssignments, t('Done'), 'success.main')}
                            </Grid>
                        </StyledSectionCardContent>
                    </StyledSectionCard>
                </StyledRootContainer>
            </StyledDialogContent>

            <StyledDialogActions className={`${direction}`}>
                <StyledCancelButton
                    variant="outlined"
                    color="inherit"
                    startIcon={<CloseIcon />}
                    onClick={onClose}
                >
                    {t('Cancel')}
                </StyledCancelButton>
                <StyledPdfButton
                    onClick={handleDownloadPdf}
                    variant="contained"
                    startIcon={<PdfIcon />}
                    disabled={loading}
                >
                    {loading ? <CircularProgress size={20} /> : t('DownloadPdf')}
                </StyledPdfButton>
                <StyledExcelButton
                    onClick={handleDownloadExcel}
                    variant="contained"
                    startIcon={<ExcelIcon />}
                    disabled={loading}
                >
                    {loading ? <CircularProgress size={20} /> : t('DownloadExcel')}
                </StyledExcelButton>
            </StyledDialogActions>
        </StyledDialog>
    );
};

export default AssignmentReportManager; 